import type { ReactNode } from 'react';

import { cn } from '../lib/cn';

export interface EmptyStateProps {
  title: string;
  description?: ReactNode;
  icon?: ReactNode;
  /** A primary action, when there is a sensible next step. */
  action?: ReactNode;
  className?: string;
  size?: 'sm' | 'md';
}

/**
 * "There is nothing here."
 *
 * Distinct from `ErrorState` on purpose, and the distinction is load-bearing in this
 * product. An empty list can mean the firm has no matching records OR that every
 * matching record is outside the reader's scope — and the backend deliberately makes
 * those indistinguishable (a scoped queryset simply returns fewer rows). So an empty
 * state must never say "you may not have permission to see these": that would leak
 * exactly what the scoping is designed to hide. Say what is here, and what to do.
 */
export function EmptyState({
  title,
  description,
  icon,
  action,
  className,
  size = 'md',
}: EmptyStateProps) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center text-center',
        size === 'sm' ? 'gap-2 px-4 py-8' : 'gap-3 px-6 py-14',
        className,
      )}
    >
      {icon && <div className="text-ink-subtle">{icon}</div>}
      <h3 className={cn('font-semibold text-ink', size === 'sm' ? 'text-base' : 'text-lg')}>
        {title}
      </h3>
      {description && (
        <p className="max-w-sm text-balance text-sm text-ink-muted">{description}</p>
      )}
      {action && <div className="mt-1">{action}</div>}
    </div>
  );
}
